import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';

import ModalPage from '../common/NonConnectedModalPage';
import RoundedButton from '../common/RoundedButton';
import Spinner from '../common/Spinner';

import locationShape from '../common/locationShape';
import AppError from '../services/AppError';
import { noop, parseSearch } from '../services/sharedUtils';
import tr, { site, urlLang } from '../localization/translate';
import TrTitle, { PageCategories } from '../common/TrTitle';
import trace from '../services/trace';
import { activateAccount } from '../services/iggloBack';
import observeUnmounting from '../services/observeUnmounting';

import illustration from '../assets/images/account-activated.svg';
import s from './ActivateAccount.scss';

export const ACCOUNT_ACTIVATED = 'ACCOUNT_ACTIVATED';

function notifyTabsAboutProfileActivation(email) {
  try {
    localStorage.setItem(ACCOUNT_ACTIVATED, email);
    localStorage.removeItem(ACCOUNT_ACTIVATED);
  } catch (e) {
    trace.warn('LocalStorage is most likely disabled', e);
  }
}

function goToIggloTarget() {
  if (site === 'rent') {
    return '/';
  }

  const { protocol, host } = window.location;
  return `${protocol}//${host}/rent/${urlLang}/`;
}

class ActivateAccount extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      working: true,
      query: parseSearch(props.location.search)
    };

    this.unsubscribeOnUnmount = observeUnmounting(this);
  }

  componentDidMount() {
    this.sendActivationRequest();
  }

  getContent() {
    const info = this.state.info;

    if (this.state.error) {
      if (this.state.error instanceof AppError) {
        switch (this.state.error.errorCode) { // eslint-disable-line default-case
          case 'user_already_validated': return tr('activate_account.already_validated');
          case 'user_unregistered': return tr('activate_account.no_such_user', { email: this.state.query.email });
        }
      }

      return tr('activate_account.activation_failed');
    } if (info) {
      if (info.isTenant && info.isLandlord) {
        return tr('activate_account.busy_beaver_is_active');
      } if (info.isTenant) {
        if (info.hasCircles) {
          return tr('activate_account.tenant_and_circles_are_active');
        }

        return tr('activate_account.tenant_is_active');
      } if (info.isLandlord) {
        if (info.hasProperties) {
          return tr('activate_account.landlord_and_properties_are_active');
        }

        return tr('activate_account.landlord_is_active');
      }
    }

    return tr('activate_account.activation_in_progress');
  }

  sendActivationRequest() {
    this.unsubscribeOnUnmount(
      activateAccount(this.state.query.email, this.state.query.token)
        .subscribe(
          info => this.setState({ info, working: false }),
          error => this.setState({ error, working: false }),
          () => {
            this.props.onProfileActivated(this.state.query.email);
            notifyTabsAboutProfileActivation(this.state.query.email);
          }
        )
    );
  }

  showGoToIggloButton() {
    const state = this.state;
    if (!state.working && !state.error && state.info && state.info.isLandlord) {
      return true;
    } if (this.state.error && this.state.error.errorCode === 'user_already_validated') {
      return true;
    }

    return false;
  }

  render() {
    return (
      <ModalPage>
        <TrTitle category={PageCategories.EMAIL} name="Activate account" title="activate_account" />

        <h1 className="title_top center bottom_margin_36">{ tr('activate_account.title') }</h1>

        { this.state.working ?
          <Spinner styleName="spinner" fadeIn={0} /> :
          <img role="presentation" styleName="image" src={illustration} />}

        <p className="text center tall_lines top_margin_36">{ this.getContent() }</p>

        <RoundedButton
          className={!this.showGoToIggloButton() ? s.hide : ''}
          styleName="go_to"
          to={goToIggloTarget()}
          rawLink={site === 'tenant'}
        >
          { tr('activate_account.go_to_igglo') }
        </RoundedButton>
      </ModalPage>
    );
  }
}

ActivateAccount.propTypes = {
  location: locationShape.isRequired,
  onProfileActivated: PropTypes.func
};

ActivateAccount.defaultProps = {
  onProfileActivated: noop
};

export default ActivateAccount;
